import { useReactiveVar } from '@apollo/client';

import {
  AuthenticationState,
  useCurrentUser,
  RedirectState,
  isAccountingFirmUser,
  useCurrentCompanyIdOptional,
  Path,
  getPath,
  useCompanies,
  useAllFiscalYears,
} from '@neo.tax/shared';

export const useCompaniesRedirect = (): RedirectState => {
  const {
    authenticationServiceState: { authenticationUser, loading: authenticationUserLoading },
  } = useReactiveVar(AuthenticationState.authenticationStateVar);
  const { loading: userLoading, user } = useCurrentUser();
  const { loading: companiesLoading } = useCompanies();
  const currentCompanyId = useCurrentCompanyIdOptional();
  const { loading: fiscalYearsLoading } = useAllFiscalYears({ skip: !authenticationUser });

  if (authenticationUserLoading) {
    return { isLoading: true };
  }
  if (!authenticationUser) {
    return { isLoading: false, redirectTo: getPath({ path: Path.LOGIN }) };
  }

  const loading = userLoading || companiesLoading || fiscalYearsLoading;
  if (loading) {
    return { isLoading: true };
  }
  if (!user) {
    return { isLoading: false, redirectTo: getPath({ path: Path.LOGIN }) };
  }
  if (!user.organization && user.companies.length === 0) {
    return { isLoading: false, redirectTo: getPath({ path: Path.PROFILE }) };
  }
  if (isAccountingFirmUser(user)) {
    return { isLoading: false };
  }

  // If the user has only one company, and they are not on it, redirect to it
  if (user.companies.length === 1) {
    const company = user.companies[0];
    if (company.id !== currentCompanyId) {
      return {
        isLoading: false,
        redirectTo: getPath({ path: Path.COMPANY, companyId: company.id }),
      };
    }
  }

  return {
    isLoading: false,
  };
};
